import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  typography: {
    fontFamily: 'Inter, Arial, sans-serif',
    h1: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 700,
      fontSize: '40px',
      lineHeight: '48px',
    },
    h2: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 600,
      fontSize: '32px',
      lineHeight: '42px',
    },
    h3: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '28px',
      lineHeight: '39px',
    },
    h4: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '22px',
      lineHeight: '31px',
    },
    h6: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '17px',
      lineHeight: '22px',
      letterSpacing: '-0.408px',
      fontStyle: 'normal',
    },
    h6GrayUppercase: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '11px',
      lineHeight: '15px',
      color: '#EBEBF599',
      textTransform: 'uppercase',
      alignSelf: 'flex-start',
    },
    body1: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '13px',
      lineHeight: '20px',
    },
    body2: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '21px',
    },
    caption: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '20px',
    },
    button: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '25px',
      textTransform: 'none',
    },
    overline: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '32px',
      textTransform: 'none',
    },
    title1: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '28px',
      lineHeight: '34px',
      textTransform: 'none',
    },
    navBar: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      fontStyle: 'normal',
      letterSpacing: '-0.4px',
    },
    title2: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 600,
      fontSize: '20px',
      lineHeight: '24px',
      textTransform: 'none',
      letterSpacing: '0.38px',
    },
    title3: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '22px',
      lineHeight: '28px',
      textTransform: 'none',
    },
    title4: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '11px',
      lineHeight: '13px',
      letterSpacing: '-0.408px',
      fontStyle: 'normal',
    },
    title5: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 600,
      fontSize: '24px',
      lineHeight: '24px',
      textTransform: 'none',
      letterSpacing: '0.38px',
    },
    subtitle1: {
      color: '#EBEBF599',
    },
    subtitle2: {
      color: '#EBEBF599',
    },
    bodyRegular: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '17px',
      lineHeight: '22px',
      textTransform: 'none',
      letterSpacing: '-0.408px',
    },
    bodyEmphasized: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 600,
      fontSize: '17px',
      lineHeight: '22px',
      textTransform: 'none',
      display: 'block',
      letterSpacing: '-0.408px',
    },
    footNoteEmphasized: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 600,
      fontSize: '13px',
      lineHeight: '18px',
      textTransform: 'none',
      display: 'block',
    },
    footNoteRegular: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '13px',
      lineHeight: '18px',
      textTransform: 'none',
    },
    calloutRegular: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '21px',
      textTransform: 'none',
    },
    subheadlineRegular: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '15px',
      lineHeight: '20px',
      fontStyle: 'normal',
      letterSpacing: '-0.4px',
    },
    largeTitleRegular: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '34px',
      lineHeight: '41px',
      fontStyle: 'normal',
      letterSpacing: '-0.4px',
      textAlign: 'center',
      fontFeatureSettings: "'liga' off, 'clig' off",
    },
    labelLarge: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '20px',
      fontStyle: 'normal',
      letterSpacing: '0.10000000149011612',
      textAlign: 'center',
      fontFeatureSettings: "'liga' off, 'clig' off",
    },
    chipTitle: {
      fontFamily: 'Inter, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '15px',
      lineHeight: '20px',
      letterSpacing: '-0.4px',
      fontStyle: 'normal',
      fontFeatureSettings: "'liga' off, 'clig' off",
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#1C1C1E',
    },
    secondary: {
      main: '#38383A',
    },
    error: {
      main: '#FF453A',
    },
    warning: {
      main: '#FFD60A',
    },
    info: {
      main: '#0A84FF',
    },
    success: {
      main: '#2E7D32',
      dark: '#1B5E20',
    },
    background: {
      default: '#000000',
      paper: '#000000',
      hover: '#1C1C1E',
      lightHover: '#3C3C4399',
      secondary: '#1C1C1E',
      secondaryHover: '#3A3A3C',
      secondarySelected: '#3A3A3C',
      selected: '#2C2C2E',
      translucentBlue: '#0A84FF26',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#0A84FF',
      secondaryHover: '#3692ff',
      tertiary: '#D3D3D3',
    },
  },
  colors: {
    gray: '#EBEBF599',
    green: '#30D158',
    blue: '#0A84FF',
    red: '#FF453A',
    intenseRed: '#FF375F',
    white: '#FFFFFF',
    yellow: '#FFD60A',
    grayDividerColor: '#8E8E93',
    borderBottom: '#545458A6',
    softGray: '#7676803D',

    // Employee Status Colors
    status: {
      clockedIn: '#34C759',      // Bright green for clocked in
      breakCompleted: '#34C759', // Same green as clocked in
      onBreak: '#0A84FF',        // Bright blue for on break
      breakDue: '#FFD60A',       // Bright yellow for break due
      breakOverdue: '#FF9500',   // Orange for break overdue
      late: '#FF453A',           // Red for late
      upcoming: '#5AC8FA',       // Bright light blue for upcoming
      clockedOut: '#8E8E93',     // Bright gray for off duty
    },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          border: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: '#1C1C1E',
          color: '#007BFF',
          fontSize: '14px',
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 400,
          '&:hover': {
            backgroundColor: '#3A3A3C',
            color: 'white',
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
          },
        },
      },
      variants: [
        {
          props: { variant: 'primary' },
          style: {
            backgroundColor: '#007AFF',
            color: '#FFFFFF',
            borderRadius: '12px',
            padding: '7px 14px',
            '&:hover': {
              backgroundColor: '#005BB5',
            },
          },
        },
        {
          props: { variant: 'secondary' },
          style: {
            backgroundColor: '#0A84FF26',
            color: '#0A84FF',
            borderRadius: '12px',
            padding: '7px 14px',
            '&:hover': {
              backgroundColor: '#005BB5',
            },
          },
        },
      ],
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          boxShadow: 'none',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: theme.colors.blue,
            '&:hover, &:focus': {
              backgroundColor: theme.palette.text.secondaryHover,
            },
          },
          '&:hover': {
            backgroundColor: theme.palette.background.lightHover,
          },
        }),
      },
    },
  },
  spacing: 8,
});

export default theme;
